"use client";

import type { RefObject } from "react";
import { useEffect, useState } from "react";

export interface GanttTooltipDimensions {
  width: number;
  height: number;
}

const INITIAL_TOOLTIP_DIMENSIONS: GanttTooltipDimensions = {
  width: 200,
  height: 96,
};
const TOOLTIP_DIMENSION_EPSILON = 0.5;

interface UseGanttTooltipDimensionsParams {
  tooltipRef: RefObject<HTMLDivElement | null>;
  tooltipContentTaskLabel: string | undefined;
}

export function useGanttTooltipDimensions({
  tooltipRef,
  tooltipContentTaskLabel,
}: UseGanttTooltipDimensionsParams): GanttTooltipDimensions {
  const [tooltipDimensions, setTooltipDimensions] = useState(
    INITIAL_TOOLTIP_DIMENSIONS,
  );

  useEffect(() => {
    const tooltipNode = tooltipRef.current;
    if (!tooltipNode || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const nextWidth = entry.contentRect.width;
      const nextHeight = entry.contentRect.height;

      setTooltipDimensions((previousDimensions) => {
        if (
          Math.abs(previousDimensions.width - nextWidth) <
            TOOLTIP_DIMENSION_EPSILON &&
          Math.abs(previousDimensions.height - nextHeight) <
            TOOLTIP_DIMENSION_EPSILON
        ) {
          return previousDimensions;
        }

        return {
          width: nextWidth,
          height: nextHeight,
        };
      });
    });

    resizeObserver.observe(tooltipNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [tooltipContentTaskLabel, tooltipRef]);

  return tooltipDimensions;
}
